<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="component-title-row">
        <h3><i class="fa fa-fw fa-calendar"></i> {{ _('Reservations') }}</h3>
        <div class="component-title-buttons">
          <div v-if="isFloatingReceiptAvailable">
            <button class="newSelector btn btn-success addbutton" type="button" v-on:click.stop="newOrder"><i class="fa fa-fw fa-plus"></i><span class="hidden-xs"> {{ _('Create new...') }}</span></button>
          </div>
        </div>
      </div>
      <div class="flex-row space-between">
        <div class="flex-row">
          <div class="filtercol">
            <div class="form-group">
              <div class="input-group">
                <input class="form-control" id="searchField" type="text" v-model="search" :placeholder="_('Keyword search')" />
                <span class="input-group-btn">
                  <button type="button" class="btn btn-success"><i class="fa fa-fw fa-search"></i></button>
                </span>
              </div>
            </div>
          </div>
          <div class="filtercol" v-if="!showExactRange">
            <div class="form-group">
              <div class="input-group">
                <div class="input-group-addon">{{ _('Date range') }}</div>
                <select class="form-control" v-model="range">
                  <option value="overview" selected>{{ _('Overview') }}</option>
                  <option value="startingtoday">{{ _('Starting today') }}</option>
                  <option value="startingtomorrow">{{ _('Starting tomorrow') }}</option>
                  <option value="startingthisweek">{{ _('Starting in a week') }}</option>
                  <option value="startingthismonth">{{ _('Starting in a month') }}</option>
                  <option value="starting">{{ _('All upcoming') }}</option>
                  <option value="endingtoday">{{ _('Ending today') }}</option>
                  <option value="endingtomorrow">{{ _('Ending tomorrow') }}</option>
                  <option value="endingthisweek">{{ _('Ending in a week') }}</option>
                  <option value="endingthismonth">{{ _('Ending in a month') }}</option>
                  <option value="all">{{ _('All reservations') }}</option>
                </select>
              </div>
            </div>
          </div>
          <div v-if="showExactRange">
            <div class="rangeFields">
              <div class="form-inline">
                <div class="form-group">
                  <DatePicker v-model="dateStart" :shortLocale="shortLocale" :staticInput="true" :placeholder="_('Select start date')"></DatePicker>
                </div>
                <label for="endDate"> - </label>
                <div class="form-group">
                  <DatePicker v-model="dateEnd" :shortLocale="shortLocale" :staticInput="true" :placeholder="_('Select end date')"></DatePicker>
                </div>
              </div>
            </div>
          </div>
          <div class="filtercol">
            <button id="selectRange" type="button" class="btn btn-default" v-on:click="selectRange" v-if="!showExactRange"><div class=""></div>{{ _('Select exact date range') }}</button>
            <button id="unselectRange" type="button" class="btn btn-default" v-on:click="unselectRange" v-else><div class=""></div>{{ _('Cancel') }}</button>
          </div>
        </div>
        <div class="filtercol">
          <button class="btn btn-success" v-on:click="downloadXlsx"><i class="fa fa-fw fa-download"></i><span class="hidden-xs hidden-sm hidden-md">{{ _('Download Excel file') }}</span></button>
        </div>
      </div>
      <Datatable :url="reservationsUrl" :search="search" :start-date-filter="startDateFilter" :end-date-filter="endDateFilter" :options="tableOptions" ref="datatable" v-on:data="tableEvent" />
    </div>
  </div>
</template>
<script>

import components from '~/lib/components'
import formatters from '~/lib/formatters'
import { DateTime } from 'luxon'
import Datatable from '~/components/Datatable'
import debounce from 'lodash/debounce'

export default {
  data () {
    const $this = this
    const _ = useNuxtApp().$translate
    return {
      showExactRange: false,
      dateStart: false,
      dateEnd: false,
      search: '',
      range: 'overview',
      reservations: [],
      tableOptions: {
        rowFormatter (row) {
          const container = document.createElement('div')
          const block = document.createElement('blockquote')
          const data = row.getData()
          if (!data || !data.sharedResource) {
            return
          }
          for (let i = 0; i < data.sharedResource.length; i++) {
            const resource = data.sharedResource[i]
            const link = document.createElement('a')
            link.href = 'javascript:;'
            link.classList.add('label', 'label-default')
            link.innerHTML = '<strong>' + $this._('Resource') + ':</strong> ' + resource.name
            link.onclick = $this.editResource(resource)
            block.appendChild(link)
          }
          container.classList.add('reservation-resources')
          container.appendChild(block)
          row.getElement().appendChild(container)
        },
        responsiveLayoutCollapseFormatter: $this.collapseFormatter(),
        columns: [{
          title: _('Reservation starts'),
          field: 'start_ts',
          widthGrow: 1,
          formatter: $this.dateAndTimeFormatter(),
          responsive: 0
        },
        {
          title: _('Reservation ends'),
          field: 'end_ts',
          formatter: $this.dateAndTimeFormatter(),
          widthGrow: 1
        },
        {
          title: _('Reserved product'),
          field: 'product_name',
          widthGrow: 2,
          responsive: 0
        },
        {
          title: _('Customer'),
          field: 'client_name',
          widthGrow: 2
        },
        {
          title: _('Amount'),
          field: 'resource_count',
          width: 110
        },
        {
          title: _('Persons'),
          field: 'count',
          width: 110
        },
        {
          title: _('ID'),
          field: 'id',
          width: 80
        },
        {
          title: _('More details'),
          field: 'client_more',
          widthGrow: 2
        },
        ],
        initialSort: [{
          column: 'start_ts',
          dir: 'asc'
        }],
        rowClick (e, row) {
          $this.editRow(row)
        }
      }
    }
  },
  mixins: [components, formatters],
  computed: {
    reservationsUrl () {
      return this.baseDomain + this.shopId + '/reservations.json?details=true&apikey=' + this.apiKey + '&rawformat=true'
    },
    modalId () {
      return this.column.identifier + '-component-' + this.component.id + '-reservationlist'
    },
    startDateFilter: {
      get () {
        if (this.dateStart && this.dateEnd) {
          return DateTime.fromJSDate(this.dateStart, { zone: this.timezone }).toFormat('d.M.yyyy') + '-' + DateTime.fromJSDate(this.dateEnd, { zone: this.timezone }).toFormat('d.M.yyyy')
        }
        switch (this.range) {
          case 'startingtoday':
          case 'startingtomorrow':
          case 'startingthisweek':
          case 'startingthismonth':
          case 'starting':
          case 'overview':
            return this.range
        }
        return ''
      },
      set (newValue) {
        if (!newValue || !newValue.match || !newValue.match('-')) {
          switch (newValue) {
            case 'startingtoday':
            case 'startingtomorrow':
            case 'startingthisweek':
            case 'startingthismonth':
            case 'starting':
            case 'overview':
              this.range = newValue
              break
          }
          this.dateStart = false
          this.dateEnd = false
          this.showExactRange = false
          return
        }
        this.setExactRangeFromFilter(newValue)
      }
    },
    endDateFilter: {
      get () {
        switch (this.range) {
          case 'endingtoday':
          case 'endingtomorrow':
          case 'endingthisweek':
          case 'endingthismonth':
          case 'all':
            return this.range
        }
        return ''
      },
      set (newValue) {
        switch (newValue) {
          case 'endingtoday':
          case 'endingtomorrow':
          case 'endingthisweek':
          case 'endingthismonth':
          case 'all':
            this.range = newValue
            break
        }
      }
    }
  },
  methods: {
    newOrder () {
      this.EventBus.$emit('showFloatingReceipt')
    },
    isOdd (i) {
      return i % 2
    },
    editResource (resource) {
      return (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'ResourceDetails',
          title: this._('Resource') + ' ' + resource.id + ': ' + resource.name,
          width: 6,
          data: {
            resourceId: resource.id
          }
        })
      }
    },
    editRow (row) {
      const id = row.getCell('id').getValue()
      const name = row.getCell('client_name').getValue()
      this.editReservation(id, name)
    },
    editReservation (id, name, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'ReservationDetails',
        title: this._('Reservation') + ' ' + id + (name ? ': ' + name : ''),
        width: 6,
        data: {
          reservationId: id
        },
        skipState
      })
    },
    reload () {
      this.$refs.datatable.reload()
    },
    selectRange () {
      this.showExactRange = true
    },
    unselectRange () {
      this.showExactRange = this.dateStart = this.dateEnd = false
    },
    inputDateStart (newValue) {
      this.dateStart = newValue
    },
    inputDateEnd (newValue) {
      this.dateEnd = newValue
    },
    downloadXlsx () {
      const filters = []
      if (this.search) {
        filters.push({ field: 'search', type: 'like', value: this.search })
      }
      if (this.startDateFilter) {
        filters.push({ field: 'start_ts', type: '=', value: this.startDateFilter })
      }
      if (this.endDateFilter) {
        filters.push({ field: 'end_ts', type: '=', value: this.endDateFilter })
      }
      let filterStr = ''
      if (filters.length) {
        filterStr = '&filters[]=' + filters.map(f => JSON.stringify(f)).join('&filters[]=')
      }
      window.open(this.baseDomain + this.shopId + '/reservations.xlsx?apikey=' + this.apiKey + filterStr)
    }
  },
  components: {
    Datatable
  },
  mounted () {
    if(!this.events) {
      let events = {}
      events['reservationUpdate'] = debounce((message) => {
        return this.reload()
      }, 200)
      events['restoreDetails'] = (message) => {
        if (message.shopId !== this.shopId) {
          return
        }
        switch(message.component) {
          case 'ReservationDetails':
            this.editReservation(message.data.reservationId, null, true)
            break
        }
      }
      events['setReservationListParams'] = (message) => {
        if (message) {
          if (message.range) {
            this.range = message.range
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }

}

</script>
<style scoped>
.dark {
  background-color: #ECEFF1;
}
</style>
<style>
.reservation-resources .label {
  color: #fff;
}
.reservation-resources .label + .label {
  margin-left: 4px;
}
</style>